/**
 * Partnership Contact Form Validation
 *
 * This module provides comprehensive client-side validation for the partnership
 * contact form, including input constraints and validation rules that align with
 * server-side GraphQL scalar types (Email, PhoneNumber).
 */

// Field length constraints - Based on common database and GraphQL scalar limits
export const FIELD_CONSTRAINTS = {
  // Name field - typically 2-100 characters for full names
  NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 100,
  },
  // Email field - RFC 5321 maximum is 320 characters
  EMAIL: {
    MIN_LENGTH: 5, // a@b.c
    MAX_LENGTH: 320,
  },
  // Phone number field - E.164 format allows up to 15 digits
  PHONE: {
    MIN_LENGTH: 10, // US minimum (10 digits)
    MAX_LENGTH: 16, // E.164 format (+15 digits)
  },
  // Organization name - typical business name constraints
  ORGANIZATION: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 200,
  },
  // Message field - reasonable limit for form messages
  MESSAGE: {
    MIN_LENGTH: 0, // Optional field
    MAX_LENGTH: 2000,
  },
} as const;

// Validation regex patterns
const VALIDATION_PATTERNS = {
  // Enhanced email regex - more comprehensive than HTML5 default
  EMAIL:
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,

  // E.164 phone number format validation
  // Allows optional + prefix, followed by 10-15 digits (after normalization)
  // Examples: +12345678901, 12345678901
  PHONE_E164: /^\+?[1-9]\d{9,14}$/,

  // More lenient phone pattern for intermediate validation (allows formatting characters)
  PHONE_FORMATTED:
    /^(\+?[1-9]\d{9,14}|[0-9]{10}|\+?1?[\s\-\.]?[(]?[0-9]{3}[)]?[\s\-\.]?[0-9]{3}[\s\-\.]?[0-9]{4})$/,

  // US phone number validation - enforces NANP rules (area code cannot start with 0 or 1)
  US_PHONE: /^[2-9]\d{2}[2-9]\d{6}$/,

  // Name validation - letters, spaces, hyphens, apostrophes
  NAME: /^[a-zA-Z\s\-'\.]{2,}$/,

  // Organization name - alphanumeric, spaces, common business punctuation
  ORGANIZATION: /^[a-zA-Z0-9\s\-'\.&,()]+$/,
} as const;

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
  hint?: string;
}

/**
 * Validates a name field
 */
export const validateName = (name: string): ValidationResult => {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return { isValid: false, errorMessage: 'Name is required' };
  }

  if (trimmedName.length < FIELD_CONSTRAINTS.NAME.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: `Name must be at least ${FIELD_CONSTRAINTS.NAME.MIN_LENGTH} characters`,
    };
  }

  if (trimmedName.length > FIELD_CONSTRAINTS.NAME.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `Name cannot exceed ${FIELD_CONSTRAINTS.NAME.MAX_LENGTH} characters`,
    };
  }

  if (!VALIDATION_PATTERNS.NAME.test(trimmedName)) {
    return { isValid: false, errorMessage: 'Name contains invalid characters' };
  }

  return { isValid: true };
};

/**
 * Validates an email field with enhanced regex validation
 */
export const validateEmail = (email: string): ValidationResult => {
  const trimmedEmail = email.trim().toLowerCase();

  if (!trimmedEmail) {
    return { isValid: false, errorMessage: 'Business email is required' };
  }

  if (trimmedEmail.length < FIELD_CONSTRAINTS.EMAIL.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: 'Please enter a valid email address',
    };
  }

  if (trimmedEmail.length > FIELD_CONSTRAINTS.EMAIL.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `Email cannot exceed ${FIELD_CONSTRAINTS.EMAIL.MAX_LENGTH} characters`,
    };
  }

  if (!VALIDATION_PATTERNS.EMAIL.test(trimmedEmail)) {
    return {
      isValid: false,
      errorMessage: 'Please enter a valid business email address',
    };
  }

  return { isValid: true };
};

/**
 * Normalizes phone number by removing formatting characters
 */
const normalizePhoneNumber = (phone: string): string => {
  return phone.replace(/[\s\-\(\)\.]/g, '');
};

/**
 * Validates phone number with E.164 format support
 */
export const validatePhone = (phone: string): ValidationResult => {
  const trimmedPhone = phone.trim();

  if (!trimmedPhone) {
    return { isValid: false, errorMessage: 'Phone number is required' };
  }

  // First check if it matches a formatted pattern (for user experience)
  if (!VALIDATION_PATTERNS.PHONE_FORMATTED.test(trimmedPhone)) {
    return {
      isValid: false,
      errorMessage: 'Please enter a valid phone number',
    };
  }

  // Normalize the phone number for E.164 validation
  const normalizedPhone = normalizePhoneNumber(trimmedPhone);

  if (normalizedPhone.length < FIELD_CONSTRAINTS.PHONE.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: 'Phone number must be at least 10 digits',
    };
  }

  if (normalizedPhone.length > FIELD_CONSTRAINTS.PHONE.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: 'Phone number cannot exceed 17 digits',
    };
  }

  // Handle different phone number formats
  let e164Phone: string;

  if (normalizedPhone.startsWith('+')) {
    // Already has country code
    e164Phone = normalizedPhone;
  } else if (normalizedPhone.length === 10) {
    // 10 digits - treat as US number and add +1 country code
    // Validate US area code rules (area code cannot start with 0 or 1)
    if (!VALIDATION_PATTERNS.US_PHONE.test(normalizedPhone)) {
      return {
        isValid: false,
        errorMessage:
          'Please enter a valid US phone number (eg: +12345678901, 12345678901)',
      };
    }

    e164Phone = `+1${normalizedPhone}`;

    // Return with hint for 10-digit numbers without country code
    return {
      isValid: true,
      hint: 'For international numbers, please include country code (e.g., +1 for US numbers)',
    };
  } else if (normalizedPhone.length === 11 && normalizedPhone.startsWith('1')) {
    // 11 digits starting with 1 - US number with country code
    e164Phone = `+${normalizedPhone}`;
  } else {
    // International number - add + if not present
    e164Phone = `+${normalizedPhone}`;
  }

  // Validate E.164 format for non-10-digit numbers
  if (!VALIDATION_PATTERNS.PHONE_E164.test(e164Phone)) {
    return {
      isValid: false,
      errorMessage: 'Please enter a valid phone number',
    };
  }

  return { isValid: true };
};

/**
 * Validates organization name
 */
export const validateOrganization = (
  organization: string,
): ValidationResult => {
  const trimmedOrganization = organization.trim();

  if (!trimmedOrganization) {
    return { isValid: false, errorMessage: 'Organization is required' };
  }

  if (trimmedOrganization.length < FIELD_CONSTRAINTS.ORGANIZATION.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: `Organization name must be at least ${FIELD_CONSTRAINTS.ORGANIZATION.MIN_LENGTH} characters`,
    };
  }

  if (trimmedOrganization.length > FIELD_CONSTRAINTS.ORGANIZATION.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `Organization name cannot exceed ${FIELD_CONSTRAINTS.ORGANIZATION.MAX_LENGTH} characters`,
    };
  }

  if (!VALIDATION_PATTERNS.ORGANIZATION.test(trimmedOrganization)) {
    return {
      isValid: false,
      errorMessage: 'Organization name contains invalid characters',
    };
  }

  return { isValid: true };
};

/**
 * Validates optional message field
 */
export const validateMessage = (message: string): ValidationResult => {
  if (message.length > FIELD_CONSTRAINTS.MESSAGE.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `Message cannot exceed ${FIELD_CONSTRAINTS.MESSAGE.MAX_LENGTH} characters`,
    };
  }

  return { isValid: true };
};

/**
 * Validates select field (industry or location)
 */
const validateSelectField = (
  value: string,
  fieldName: string,
): ValidationResult => {
  if (!value || value.trim() === '') {
    return {
      isValid: false,
      errorMessage: `Please select ${fieldName.toLowerCase()}`,
    };
  }

  return { isValid: true };
};

/**
 * Interface for field validation errors
 */
export interface FieldValidationErrors {
  name?: string;
  email?: string;
  phone?: string;
  organization?: string;
  industry?: string;
  location?: string;
  message?: string;
}

/**
 * Validates all form fields and returns validation errors
 */
export const validatePartnershipForm = (formData: {
  name: string;
  email: string;
  phone: string;
  organization: string;
  industry: string;
  location: string;
  message: string;
}): { isValid: boolean; errors: FieldValidationErrors } => {
  const errors: FieldValidationErrors = {};

  // Validate each field
  const nameResult = validateName(formData.name);
  if (!nameResult.isValid) errors.name = nameResult.errorMessage;

  const emailResult = validateEmail(formData.email);
  if (!emailResult.isValid) errors.email = emailResult.errorMessage;

  const phoneResult = validatePhone(formData.phone);
  if (!phoneResult.isValid) errors.phone = phoneResult.errorMessage;

  const organizationResult = validateOrganization(formData.organization);
  if (!organizationResult.isValid)
    errors.organization = organizationResult.errorMessage;

  const industryResult = validateSelectField(formData.industry, 'Industry');
  if (!industryResult.isValid) errors.industry = industryResult.errorMessage;

  const locationResult = validateSelectField(formData.location, 'Location');
  if (!locationResult.isValid) errors.location = locationResult.errorMessage;

  const messageResult = validateMessage(formData.message);
  if (!messageResult.isValid) errors.message = messageResult.errorMessage;

  const isValid = Object.keys(errors).length === 0;

  return { isValid, errors };
};
